import React from "react";
import connect from "react-redux/es/connect/connect";
import { withRouter } from "react-router";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import { Auth } from "aws-amplify/lib/index";
import { Dropdown, Icon, Responsive } from "semantic-ui-react";

import { SetAdvisor } from "data/advisor/actions";
import history from "data/history";
import { callResetFromSnapshot } from "data/libs/demoHub";

import { useBooleanState } from "hooks";
import { SetAlert } from "components/Alerts/actions";
import Modal from "components/Modal";

import { useWorkDeskUrl } from "scenes/Hubly/components/Workspace/components/hooks/useWorkDeskUrl";
import WorkDeskExitConfirmationDialog from "scenes/Hubly/components/Workspace/components/WorkDeskExitConfirmationDialog";

export function NavigationDropdown(props) {
  const { pathname } = useLocation();
  const { isWorkdeskPath } = useWorkDeskUrl();
  const [isExitConfirmOpen, openExitConfirm, closeExitConfirm] =
    useBooleanState();

  const goToSettings = () => {
    const { hubId } = props;
    history.push(`/hub/${hubId}/settings`);
  };

  const [isModalOpened, openModal] = useBooleanState();

  const goToFeedback = () => {
    window.open("https://feedback.myhubly.com", "_blank");
  };

  const refresh = () => {
    return window.location.reload(true);
  };

  const resetFromSnapshot = async (snapshotHubId) => {
    const { hubId } = props;
    openModal();

    try {
      await callResetFromSnapshot({ hubId, snapshotHubId });
      refresh();
    } catch (error) {
      console.error(`Cannot reset demo hub ${error}`);
    }
  };

  const signOut = () => {
    const { userLogout } = props;
    Auth.signOut().then(() => {
      userLogout();
    });
  };

  const confirmSignOut = () => {
    if (isWorkdeskPath(pathname)) {
      openExitConfirm();
      return;
    }
    signOut();
  };

  const goToRoot = () => {
    history.push("/");
  };

  const generateMenu = () => {
    const { advisor, hubId } = props;
    const options = [];

    if (advisor.hubs.length > 1)
      options.push({
        key: "hubs",
        text: "My Hubs",
        icon: "sitemap",
        onClick: goToRoot,
      });
    const settingOption = {
      key: "settings",
      text: "Settings",
      icon: "settings",
      onClick: goToSettings,
    };

    if (hubId) {
      // Workaround to disable eslint prettier/prettier error
      options.push({
        ...settingOption,
        "data-test": "top-menu-settings",
      });
    }
    if (hubId && hubId.startsWith("demo") && !Number.isNaN(hubId.slice(-1))) {
      options.push({
        key: "resetWB",
        text: "Reset WealthBox Demo",
        icon: "sync",
        onClick: () => {
          resetFromSnapshot("demoW");
        },
      });
      options.push({
        key: "resetRT",
        text: "Reset Redtail Demo",
        icon: "sync",
        onClick: () => {
          resetFromSnapshot("demoR");
        },
      });
      options.push({
        key: "resetSF",
        text: "Reset Salesforce Demo",
        icon: "sync",
        onClick: () => {
          resetFromSnapshot("demoS");
        },
      });
    }
    options.push({
      key: "feedback",
      text: "Feature Request",
      icon: "bullhorn",
      onClick: goToFeedback,
    });
    options.push({
      key: "sign-out",
      text: "Sign Out",
      icon: "sign-out",
      onClick: confirmSignOut,
    });

    return options;
  };

  const { advisor } = props;
  const options = generateMenu();
  // TODO: Change to trigger Icon to Image when we allow advisor image uploads in HUB-767
  return (
    <React.Fragment>
      <WorkDeskExitConfirmationDialog
        open={isExitConfirmOpen}
        onStay={closeExitConfirm}
        onExit={signOut}
      />
      {isModalOpened && (
        <Modal isModalOpen>
          <Modal.Body scrolling style={{ maxHeight: "80vh" }}>
            Resetting hub...
          </Modal.Body>
        </Modal>
      )}
      <div
        className="grey_on_hover rounded"
        style={{ padding: ".25em .5em 0em .5em" }}
      >
        <Responsive
          as={Dropdown}
          icon={null}
          maxWidth={1350}
          options={options}
          pointing="top right"
          style={{ zIndex: 1000 }}
          trigger={
            <span data-test="top-menu">
              <Icon color="grey" name="user circle" size="large" />
            </span>
          }
        />
        <Responsive
          as={Dropdown}
          icon={null}
          minWidth={1351}
          options={options}
          pointing="top right"
          style={{ zIndex: 1000 }}
          trigger={
            <div
              style={{
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span data-test="top-menu">
                <Icon color="grey" name="user circle" size="large" />
                {advisor.name || "Navigation"}
              </span>
            </div>
          }
        />
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = (state) => {
  return {
    advisor: state.hubly.data.advisor,
    hubId: state.hubly.data.hub.selected.hub?.hubId,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setAdvisor: (advisor) => {
      dispatch(SetAdvisor(advisor));
    },
    setAlert: (alert) => {
      dispatch(SetAlert(alert));
    },
    userLogout: () => {
      dispatch({
        type: "USER_LOGOUT",
      });
    },
  };
};
NavigationDropdown.defaultProps = {
  hubId: undefined,
};

NavigationDropdown.propTypes = {
  advisor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    hubs: PropTypes.array.isRequired,
  }).isRequired,
  userLogout: PropTypes.func.isRequired,
  hubId: PropTypes.string,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(NavigationDropdown)
);
